import React, { useState, useEffect } from 'react'
import { Container, Pages } from '../../../../../styled'
import { Builder } from '../../../../../../../../components'
import PageTypes from './PageTypes'
import PagePreviewSection from './PagePreviewSection'
import { useSelector } from 'react-redux'
import LayoutOptions from './LayoutOptions'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Divider, Icon, message } from 'antd'
import { useTour } from '../../../../../../../../components/Tour'

// Styled components for collapse panel
const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(55.3rem + 30px + 3rem);
  min-height: 0;
  overflow: hidden;
  width: 200px;
  padding: 3rem 2rem !important;
`

const CollapsePanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: ${({ isOpen, ratio, onlyOneOpen }) => {
    if (onlyOneOpen && isOpen) return '1 1 0'
    if (ratio) return ratio
    return '0 0 auto'
  }};
`

const CollapseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  border-radius: 4px;
  font-weight: bold;
  flex-shrink: 0; /* Prevent header from shrinking */
`

const CollapseContent = styled.div`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  padding: 16px 0;
  background: #ffffff;
  overflow-y: auto;
  flex: 1 1 0;
  max-height: 100%;
`

const PageViewOption = props => {
  const { t } = useTranslation()

  const { enableNext, currentStep } = useTour()

  const defaultPageTypes = {
    introduction: t('v4:introduction'),
    story: t('v4:story_writing'),
    scenario: t('v2:scenario'),
    quiz: t('v4:Ai_quiz'),
    bulletPoints: t('v4:bullet_points'),
    infographics: t('v5:infographics'),
    interactive: t('v5:interactive_simulation'),
    summary: t('v4:summary'),
    worldScenario: t('v4:world_scenario'),
    matchImage: t('matchimage:title'),
    addContent: t('v4:add_content')
  }
  const [pageTypes, setPageTypes] = useState(defaultPageTypes)
  const [activeType, setActiveType] = useState(null)
  const [isPageTypesOpen, setIsPageTypesOpen] = useState(false)
  const [isLayoutOptionsOpen, setIsLayoutOptionsOpen] = useState(false)
  const [scale, setScale] = useState(1)
  const { currentPage, toPage } = props

  // Auto-enable next if panel is already expanded when tour reaches expand-icon step
  useEffect(() => {
    if (currentStep && currentStep.target === '[data-tour="expand-icon"]' && isPageTypesOpen) {
      enableNext()
    }
  }, [currentStep, isPageTypesOpen, enableNext])

  const onPreviewItemClick = i => {
    if (currentPage === i + 1) return
    toPage(i + 1)
  }

  const handleType = type => {
    setActiveType(type)

    if (type !== 'quiz') {
      setPageTypes(defaultPageTypes)
    }
  }

  const pageStructureData = useSelector(
    state => state.lessonBuilder.pages_landscape
  )
  const course = useSelector(state => state.courses.courseById)
  const isDev = process.env.NODE_ENV === 'development'
  const isAiGeneratedCourse = isDev
    ? true
    : course && course.item && course.item.is_ai_generated
  const courseId = course && course.item && course.item.id

  const courseLanguage = course && course.item && course.item.lang
  console.log({
    course,
    courseId,
    isAiGeneratedCourse
  })

  const calculateScale = () => {
    const baseWidth = 1550 // 155rem in pixels
    const viewportWidth = window.innerWidth
    const padding = 140 // Account for container padding (2rem on each side = 40px) + extra margin

    if (viewportWidth < baseWidth + padding) {
      const newScale = Math.max(0.5, (viewportWidth - padding) / baseWidth)
      setScale(newScale)
    } else {
      setScale(1)
    }
  }
  // Calculate scale based on viewport width
  useEffect(() => {
    calculateScale()
    window.addEventListener('resize', calculateScale)

    return () => window.removeEventListener('resize', calculateScale)
  }, [])

  // Determine if only one panel is open
  const onlyOneOpen =
    (isPageTypesOpen && !isLayoutOptionsOpen) ||
    (!isPageTypesOpen && isLayoutOptionsOpen)

  return (
    <Container noPadding>
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'left top',
          width: '155rem',
          marginBottom: scale < 1 ? `${(1 - scale) * -500}px` : '0'
        }}
      >
        <Pages>
          <Pages.List>
            {isAiGeneratedCourse && (
              <LeftSection className="page-builder-preview-container left-section">
                <CollapsePanel
                  isOpen={isPageTypesOpen}
                  ratio={
                    isPageTypesOpen ? (isLayoutOptionsOpen ? '2' : '1') : '0'
                  }
                  onlyOneOpen={onlyOneOpen}
                >
                  <CollapseHeader
                    onClick={() => setIsPageTypesOpen(!isPageTypesOpen)}
                  >
                    <span>{t('v4:generate_content')}</span>
                    <Icon
                      onClick={() => {
                        const isExpanding = !isPageTypesOpen
                        if (isExpanding && currentStep && currentStep.target === '[data-tour="expand-icon"]') {
                          enableNext()
                        }
                      }}
                      data-tour="expand-icon"
                      type={isPageTypesOpen ? 'up' : 'down'}
                    />
                  </CollapseHeader>
                  <CollapseContent isOpen={isPageTypesOpen}>
                    <div className="page-builder-type">
                      <PageTypes
                        activeType={activeType}
                        pageTypes={pageTypes}
                        onClickType={handleType}
                        pageStructureData={pageStructureData}
                        currentPage={currentPage}
                      />
                    </div>
                  </CollapseContent>
                </CollapsePanel>

                <Divider style={{ margin: '2px 0' }} />

                <CollapsePanel
                  isOpen={isLayoutOptionsOpen}
                  ratio={
                    isLayoutOptionsOpen ? (isPageTypesOpen ? '2' : '1') : '0'
                  }
                  onlyOneOpen={onlyOneOpen}
                >
                  <CollapseHeader
                    onClick={() => setIsLayoutOptionsOpen(!isLayoutOptionsOpen)}
                  >
                    <span>{t('v4:layout')}</span>
                    <Icon type={isLayoutOptionsOpen ? 'up' : 'down'} />
                  </CollapseHeader>
                  <CollapseContent isOpen={isLayoutOptionsOpen}>
                    <LayoutOptions {...props} />
                  </CollapseContent>
                </CollapsePanel>
              </LeftSection>
            )}
            <Pages.Item>
              <Builder.Page {...props} scale={scale} />
            </Pages.Item>
            <PagePreviewSection
              {...props}
              courseId={courseId}
              onPreviewItemClick={onPreviewItemClick}
              pageTypes={pageTypes}
              activeType={activeType}
              handleType={handleType}
              language={courseLanguage}
            />
          </Pages.List>
        </Pages>
      </div>
    </Container>
  )
}

export default PageViewOption
